import { useTranslation } from 'react-i18next';

import ModelAttributeCard from '@components/model/details/ModelAttributeCard';
import ModelParametersList from '@components/model/details/ModelParametersList';
import { ProtocolVariantsList } from '@components/model/protocol/ProtocolVariantsList';
import { Typography } from '@destination/components';

import useProtocol from '@api/use-protocol';
import { IModel } from '@models/IModelBasic';
import { IProtocol } from '@models/IProtocol';
import { slugify } from '@utils/text-utils';

export interface IProtocolDetailBody {
  model: IModel;
}

const ignoredModelAttributes = ['type', 'module', 'key', 'version'];

export default function ProtocolDetailBody({ model }: IProtocolDetailBody) {
  const { t } = useTranslation(undefined, { keyPrefix: 'browse.model' });
  const { protocol, isLoading } = useProtocol(model);

  const modelAttributes = protocol
    ? Object.keys(protocol)
        .filter(a => !ignoredModelAttributes.includes(a))
        .sort()
    : [];

  if (isLoading)
    return <Typography className="px-3">{t('loading')}</Typography>;

  return (
    <>
      {protocol &&
        modelAttributes.map(modelAttribute => (
          <ModelAttributeCard
            key={model.key + '-attribute-' + slugify(modelAttribute)}
            title={modelAttribute}
          >
            {renderProtocolBody(protocol, modelAttribute)}
          </ModelAttributeCard>
        ))}
    </>
  );
}

const renderProtocolBody = (protocol: IProtocol, attribute: string) => {
  switch (attribute) {
    case 'parameter_definitions':
      return (
        <ModelParametersList
          data-testid="protocol-parameters-list"
          parameters={protocol.parameter_definitions}
        />
      );
    case 'variants':
      if (!protocol.variants) return null;
      return (
        <ProtocolVariantsList
          data-testid="protocol-variants-list"
          variants={protocol.variants}
        />
      );
    default:
      return <>Not implemented yet!</>;
  }
};
